import { dateFormats, useCurrencyNetworks, useIntl, type WalletCurrencyNetwork } from '@capsicum/common';

import { useSurveyById } from './useSurveyById';

const PARTNER_LOGO = 'partner_logo';

type UseSurveyVoteScreenParams = {
	answerId: string;
	surveyId: string;
};

const getDecimalDetailFromCurrencyNetwork = (
	currencyNetwork: undefined | WalletCurrencyNetwork,
	currencySmartContractAddress: string | undefined
): number | undefined => {
	if (!currencyNetwork || !currencySmartContractAddress) {
		return undefined;
	}

	return currencyNetwork?.config?.blockchainSmartcontracts?.[currencySmartContractAddress]?.decimalDetail;
};

export const useSurveyVoteScreen = ({ answerId, surveyId }: UseSurveyVoteScreenParams) => {
	const { isSurveyLoading, surveyData, surveyError } = useSurveyById(surveyId);
	const { currencyNetwork } = useCurrencyNetworks({
		smartContractAddresses: surveyData?.currencySmartContractAddress
			? [surveyData?.currencySmartContractAddress]
			: []
	});

	const { $t, getLocalizedField } = useIntl();

	const isLoading = isSurveyLoading;
	const isError = surveyError || !surveyData;

	if (isLoading || isError) {
		return {
			isError,
			isLoading
		};
	}

	const partnerLogo = surveyData.images?.find(image => image.imageType?.type === PARTNER_LOGO)?.url || '';
	const closeAt = dateFormats.format(surveyData.closeAt, 'DD/MM/YYYY');

	const currencyShortName = surveyData.currencyShortName ?? undefined;
	const currencyDecimalDetail = getDecimalDetailFromCurrencyNetwork(
		currencyNetwork,
		surveyData.currencySmartContractAddress
	);

	const stakingContractAddress = surveyData.stakingSmartContractAddress;
	const surveyContractAddress = surveyData.blockchainAddress;
	const tokenLimit = surveyData.tokenLimit;
	const tokenContractAddress = surveyData.currencySmartContractAddress;

	const surveyQuestion = getLocalizedField('question', surveyData);
	const answers = surveyData.answers || [];
	const selectedAnswerText = answers.find(answer => answer.id === answerId)?.answer || '';
	const selectedAnswerId = answers.find(answer => answer.id === answerId)?.id || '';

	const intlTitle = $t('survey.vote.title');
	const intlConfirm = $t('survey.vote.button');

	return {
		closeAt,
		currencyDecimalDetail,
		currencyShortName,
		intlConfirm,
		// TRANSLATIONS
		intlTitle,
		isError,
		// GET request(s)
		isLoading,
		partnerLogo,
		selectedAnswerId,
		selectedAnswerText,
		stakingContractAddress,
		surveyContractAddress,
		surveyQuestion,
		tokenContractAddress,
		tokenLimit
	};
};
